import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
} from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services'
import { STORAGE_KEYS, ROUTE_PATHS } from '../constants/app.constants'
import { decodeJWT, extractRolesFromToken } from '../utils/jwt.utils'
import { useProactiveTokenRefresh } from './auth/use-proactive-token-refresh'
import {
  BACKOFFICE_ROLES,
  CONTENT_ROLES,
  TICKETING_HOME,
  TICKETING_READ_ROLES,
  hasAnyRole,
} from '../constants/roles'

// Allowed roles for backoffice access: the content roles, plus the ticketing-only roles
// (`ticketing_admin`, `ticketing_finance`), who are confined to the Ticketing section by
// `canAccessContent` below (AppContent redirects them, useNavigation hides the rest).
const ALLOWED_ROLES = BACKOFFICE_ROLES

/**
 * Check if user has backoffice access based on JWT token
 * @param token - JWT token string
 * @returns true if user has a content role or a ticketing role
 */
const hasBackofficeAccess = (token: string | null): boolean => {
  if (!token) {
    return false
  }

  const roles = extractRolesFromToken(token)

  // Check if user has any of the allowed roles
  return roles.some((role) => ALLOWED_ROLES.includes(role))
}

interface AuthContextType {
  user: Record<string, any> | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  /** The token's roles. */
  roles: string[]
  /** administrator / content_admin: every content screen (articles, events, …). */
  canAccessContent: boolean
  /** Any role that may read the Ticketing section. */
  canAccessTicketing: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<Record<string, any> | null>(() => {
    const savedUser = localStorage.getItem(STORAGE_KEYS.USER_DATA)
    return savedUser ? JSON.parse(savedUser) : null
  })
  const [token, setToken] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN)
  })
  const navigate = useNavigate()

  // Keeps the session alive in the background — refreshes the access token
  // before it expires so an open tab doesn't get bounced to login.
  useProactiveTokenRefresh()

  const login = useCallback(
    async (username: string, password: string) => {
      const response = await authService.login({ username, password })
      // Support both 'token' and 'access_token' for backward compatibility
      const accessToken = response.token || response.access_token

      if (!accessToken) {
        throw {
          message: 'Authentication failed: Missing access token.',
          status: 500,
        }
      }

      // Check roles from token before storing
      if (!hasBackofficeAccess(accessToken)) {
        throw {
          message: 'You do not have permission to access the back office.',
          status: 403,
        }
      }

      // Decode token to get user data
      const decoded = decodeJWT(accessToken)
      if (!decoded?.user) {
        throw {
          message: 'Authentication failed: Invalid token payload.',
          status: 500,
        }
      }

      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken)
      setToken(accessToken)

      // Store the refresh token too (if the backend issued one) so the
      // session can be silently renewed instead of hard-logging out on expiry.
      if (response.refresh_token) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, response.refresh_token)
      }

      // Store decoded user from token for consistency
      const userData = decoded.user
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData))
      setUser(userData)

      // Ticketing-only staff have no dashboard (its analytics API is content-only).
      navigate(
        hasAnyRole(extractRolesFromToken(accessToken), CONTENT_ROLES)
          ? ROUTE_PATHS.DASHBOARD
          : TICKETING_HOME
      )
    },
    [navigate]
  )

  const logout = useCallback(() => {
    setUser(null)
    setToken(null)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA)
    localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN)
    // The refresh token is the ONE thing that must not survive logout — if it
    // did, the next API call's proactive refresh could silently mint a fresh
    // access token and the session would never actually end.
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
    navigate(ROUTE_PATHS.LOGIN)
  }, [navigate])

  // Check authorization based on token
  const isAuthorized = hasBackofficeAccess(token)

  useEffect(() => {
    if (token && !isAuthorized) {
      logout()
    }
  }, [token, isAuthorized, logout])

  const isAuthenticated = Boolean(token && isAuthorized)

  const roles = useMemo(() => (token ? extractRolesFromToken(token) : []), [token])
  const canAccessContent = isAuthenticated && hasAnyRole(roles, CONTENT_ROLES)
  const canAccessTicketing = isAuthenticated && hasAnyRole(roles, TICKETING_READ_ROLES)

  const value = useMemo(
    () => ({
      user,
      token,
      login,
      logout,
      isAuthenticated,
      roles,
      canAccessContent,
      canAccessTicketing,
    }),
    [user, token, login, logout, isAuthenticated, roles, canAccessContent, canAccessTicketing]
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
